import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaArrowLeft,
  FaSpinner,
  FaUserPlus,
} from 'react-icons/fa';

import api from '../services/api';

const Register = () => {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');
  const [ward, setWard] = useState('');
  const [area, setArea] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] =
    useState('');

  const [loading, setLoading] = useState(false);

  /* =====================================================
     VALIDATION
  ===================================================== */

  const validateForm = () => {
    const cleanName = name.trim();
    const cleanMobile = mobile.trim();
    const cleanAddress = address.trim();
    const cleanWard = ward.trim();
    const cleanArea = area.trim();

    /* Required fields */

    if (
      !cleanName ||
      !cleanMobile ||
      !cleanAddress ||
      !cleanWard ||
      !cleanArea ||
      !password ||
      !confirmPassword
    ) {
      toast.error('Please fill all fields.');
      return false;
    }

    /* Name */

    if (cleanName.length < 2) {
      toast.error(
        'Name must contain at least 2 characters.'
      );
      return false;
    }

    if (cleanName.length > 50) {
      toast.error(
        'Name cannot exceed 50 characters.'
      );
      return false;
    }

    if (!/^[A-Za-zÀ-ÿ\s.'-]+$/.test(cleanName)) {
      toast.error(
        'Name can contain letters, spaces, apostrophes and hyphens only.'
      );
      return false;
    }

    /* Mobile */

    if (!/^[6-9][0-9]{9}$/.test(cleanMobile)) {
      toast.error(
        'Enter a valid 10-digit mobile number.'
      );
      return false;
    }

    /* Address */

    if (cleanAddress.length < 5) {
      toast.error(
        'Please enter a valid address.'
      );
      return false;
    }

    /* Ward */

    if (cleanWard.length < 1) {
      toast.error(
        'Please enter your ward.'
      );
      return false;
    }

    /* Area */

    if (cleanArea.length < 2) {
      toast.error(
        'Please enter a valid area.'
      );
      return false;
    }

    /* Password */

    if (password.length < 8) {
      toast.error(
        'Password must contain at least 8 characters.'
      );
      return false;
    }

    if (!/[A-Z]/.test(password)) {
      toast.error(
        'Password must contain at least one uppercase letter.'
      );
      return false;
    }

    if (!/[a-z]/.test(password)) {
      toast.error(
        'Password must contain at least one lowercase letter.'
      );
      return false;
    }

    if (!/[0-9]/.test(password)) {
      toast.error(
        'Password must contain at least one number.'
      );
      return false;
    }

    /* Confirm password */

    if (password !== confirmPassword) {
      toast.error(
        'Passwords do not match.'
      );
      return false;
    }

    return true;
  };

  /* =====================================================
     REGISTER CORPORATOR
  ===================================================== */

  const handleRegister = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const cleanMobile = mobile.trim();

      const payload = {
        name: name.trim(),
        mobile: cleanMobile,
        address: address.trim(),
        ward: ward.trim(),
        area: area.trim(),

        // Required by PendingRegistration
        password,

        // This app is Corporator-only
        role: 'CORPORATOR',
      };

      console.log('CORPORATOR REGISTER DATA =>', {
        ...payload,
        password: '********',
      });

      const res = await api.post(
        '/api/auth/register',
        payload
      );

      console.log(
        'CORPORATOR REGISTER RESPONSE =>',
        res.data
      );

      if (!res.data?.success) {
        toast.error(
          res.data?.message ||
            'Registration failed'
        );
        return;
      }

      toast.success(
        res.data?.message ||
          'OTP sent successfully.'
      );

      /*
       * Keep mobile number so the next authentication
       * step can use it.
       *
       * Your Corporator Login/OTP screen can read:
       * location.state.mobile
       */

      navigate('/login', {
        state: {
          mobile: cleanMobile,
          fromRegistration: true,
        },
      });

    } catch (err) {
      console.log(
        'CORPORATOR REGISTER ERROR =>',
        err?.response?.data ||
          err?.message ||
          err
      );

      if (err?.response?.status === 409) {
        toast.error(
          err?.response?.data?.message ||
            'Mobile number already registered.'
        );
        return;
      }

      if (err?.response?.status === 429) {
        toast.error(
          err?.response?.data?.message ||
            'Too many requests. Please try again later.'
        );
        return;
      }

      toast.error(
        err?.response?.data?.message ||
          'Registration failed'
      );

    } finally {
      setLoading(false);
    }
  };

  /* =====================================================
     UI
  ===================================================== */

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F7F4EE',
        fontFamily:
          "'Inter', system-ui, sans-serif",
        padding: '24px',
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .rg-input:focus {
          outline: none;
          border-color: #16325C !important;
          box-shadow: 0 0 0 3px rgba(22,50,92,0.10);
        }

        .rg-btn:not(:disabled):hover {
          background: #0F2545 !important;
        }

        .rg-link:hover {
          text-decoration: underline !important;
        }
      `}</style>

      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          background: '#FFFFFF',
          borderRadius: '18px',
          overflow: 'hidden',
          boxShadow:
            '0 24px 60px -20px rgba(16,35,63,0.35)',
        }}
      >

        {/* HEADER */}

        <div
          style={{
            background:
              'linear-gradient(160deg, #16325C 0%, #0F2545 100%)',
            padding: '30px 36px',
            position: 'relative',
          }}
        >
          <Link
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: '#9FB0C7',
              fontSize: 12.5,
              textDecoration: 'none',
              marginBottom: 14,
            }}
          >
            <FaArrowLeft size={11} />
            Back to sign in
          </Link>

          <p
            style={{
              color: '#C9A227',
              fontSize: 11.5,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              fontWeight: 600,
              margin: '0 0 6px',
            }}
          >
            Municipal Portal
          </p>

          <h1
            style={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 600,
              fontSize: 24,
              color: '#F7F4EE',
              margin: 0,
            }}
          >
            Corporator registration
          </h1>
        </div>

        {/* FORM */}

        <form
          onSubmit={handleRegister}
          style={{
            padding: '30px 36px 34px',
          }}
        >

          {/* NAME */}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Full name
            </label>

            <input
              className="rg-input"
              type="text"
              value={name}
              maxLength={50}
              placeholder="Enter full name"
              disabled={loading}
              onChange={(e) =>
                setName(e.target.value)
              }
              style={inputStyle}
            />
          </div>

          {/* MOBILE */}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Mobile number
            </label>

            <input
              className="rg-input"
              type="tel"
              value={mobile}
              maxLength={10}
              placeholder="10-digit mobile"
              disabled={loading}
              inputMode="numeric"
              onChange={(e) =>
                setMobile(
                  e.target.value
                    .replace(/\D/g, '')
                    .slice(0, 10)
                )
              }
              style={inputStyle}
            />
          </div>

          {/* ADDRESS */}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Address
            </label>

            <textarea
              className="rg-input"
              value={address}
              placeholder="House / street address"
              disabled={loading}
              onChange={(e) =>
                setAddress(e.target.value)
              }
              style={{
                ...inputStyle,
                minHeight: 80,
                resize: 'vertical',
              }}
            />
          </div>

          {/* WARD */}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Ward
            </label>

            <input
              className="rg-input"
              type="text"
              value={ward}
              placeholder="Ward number or name"
              disabled={loading}
              onChange={(e) =>
                setWard(e.target.value)
              }
              style={inputStyle}
            />
          </div>

          {/* AREA */}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Area
            </label>

            <input
              className="rg-input"
              type="text"
              value={area}
              placeholder="Locality / area"
              disabled={loading}
              onChange={(e) =>
                setArea(e.target.value)
              }
              style={inputStyle}
            />
          </div>

          {/* PASSWORD */}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Password
            </label>

            <input
              className="rg-input"
              type="password"
              value={password}
              maxLength={64}
              placeholder="Create a password"
              disabled={loading}
              autoComplete="new-password"
              onChange={(e) =>
                setPassword(e.target.value)
              }
              style={inputStyle}
            />

            <small
              style={{
                display: 'block',
                marginTop: 5,
                color: '#697386',
                fontSize: 11,
              }}
            >
              Minimum 8 characters with uppercase,
              lowercase and a number.
            </small>
          </div>

          {/* CONFIRM PASSWORD */}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Confirm password
            </label>

            <input
              className="rg-input"
              type="password"
              value={confirmPassword}
              maxLength={64}
              placeholder="Re-enter password"
              disabled={loading}
              autoComplete="new-password"
              onChange={(e) =>
                setConfirmPassword(
                  e.target.value
                )
              }
              style={inputStyle}
            />
          </div>

          {/* BUTTON */}

          <button
            type="submit"
            disabled={loading}
            className="rg-btn"
            style={{
              marginTop: 10,
              width: '100%',
              background: '#16325C',
              color: '#F7F4EE',
              border: 'none',
              borderRadius: 10,
              padding: '13px 0',
              fontSize: 14.5,
              fontWeight: 600,
              cursor: loading
                ? 'default'
                : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition:
                'background 0.15s ease',
              opacity: loading ? 0.75 : 1,
            }}
          >
            {loading ? (
              <>
                <FaSpinner
                  size={14}
                  style={{
                    animation:
                      'rotate 0.8s linear infinite',
                  }}
                />
                Creating account
              </>
            ) : (
              <>
                <FaUserPlus size={13} />
                Create account
              </>
            )}
          </button>

          <p
            style={{
              marginTop: 18,
              textAlign: 'center',
              fontSize: 13.5,
              color: '#697386',
            }}
          >
            Already registered?{' '}

            <Link
              to="/login"
              className="rg-link"
              style={{
                color: '#16325C',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Sign in
            </Link>
          </p>

        </form>
      </div>
    </div>
  );
};

/* =====================================================
   STYLES
===================================================== */

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#38445C',
  display: 'block',
  marginBottom: 6,
};

const inputStyle = {
  width: '100%',
  border: '1.5px solid #DDE2EA',
  borderRadius: 10,
  background: '#FCFBF8',
  padding: '12px 14px',
  fontSize: 14,
  color: '#10233F',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

export default Register;